import { brandDetailsState, modelState } from '@/state';
import { useAsyncAtomValue } from '@/state/loadableHooks';
import type { Brand, Equipment, Model } from '@rock/types';
import { useMemo } from 'react';

/**
 * The catalog documents behind one equipment: the model it matched, and that model's brand.
 *
 * Both nameplate decodes need the same pair — the serial's brand gate and the model number's — so the
 * resolution lives here once rather than in each hook. `brandId` is an OPAQUE auto-id, so the brand can
 * only come from the Model doc; that is the whole reason the model is loaded even when a caller only
 * wants the brand.
 *
 * Either half is undefined until it resolves (unmatched equipment, unlinked model, a Brand still
 * loading). Callers treat that as "no answer", never as an error — the decode surfaces are opportunistic.
 */
export function useEquipmentCatalog(equipment: Equipment): { model: Model | undefined; brand: Brand | undefined } {
    const model = useAsyncAtomValue(modelState(equipment.details?.modelId));
    const brand = useAsyncAtomValue(brandDetailsState(model?.brandId));
    // Memoized so a caller can safely put the whole pair in a dependency array.
    return useMemo(() => ({ model, brand }), [model, brand]);
}
